import { useRef, useState } from 'react'
import { Download, Receipt, Tag } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import type { Proposta, PropostaItem } from '@/types'
import { formatCurrency } from '@/utils/format-currency'
import { downloadStudioPresentationPdf } from '@/utils/downloadStudioPresentationPdf'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  buildPublicPresentationSlides,
  type PublicPresentationSlide,
} from '@/utils/publicProposalPresentationSlides'
import { buildPublicPlanViews, publicMonthlyAmount } from '@/utils/propostaPublicScope'
import { PublicProposalPdfSlide } from '@/components/PropostaPublica/PublicProposalPdfSlide'

type Props = {
  proposta: Proposta
  items: PropostaItem[]
  empresaNome?: string
  onContratar?: () => void
  canContratar?: boolean
}

const PREVIEW_SELECTOR = '[data-proposal-slide-preview]'

async function waitForSlidePreview(index: number) {
  const started = Date.now()
  while (Date.now() - started < 2500) {
    const host = document.querySelector(`[data-public-pdf-slide="${index}"]`)
    const node = host?.querySelector(PREVIEW_SELECTOR)
    if (node instanceof HTMLElement) {
      const box = node.getBoundingClientRect()
      if (box.width >= 40 && box.height >= 40) {
        await document.fonts.ready
        return
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 40))
  }
  throw new Error('Não foi possível montar o slide da proposta.')
}

export function ProposalInvestmentPanel({
  proposta,
  items,
  empresaNome,
  onContratar,
  canContratar = true,
}: Props) {
  const plans = buildPublicPlanViews(items)
  const mensalBruto = plans.reduce((sum, plan) => sum + publicMonthlyAmount(plan.item), 0)
  const descontoValor = mensalBruto * (proposta.desconto / 100)
  const valorMensal = Math.max(0, mensalBruto - descontoValor)
  const [downloading, setDownloading] = useState(false)
  const [captureIndex, setCaptureIndex] = useState<number | null>(null)
  const slidesRef = useRef<PublicPresentationSlide[]>([])

  const handleDownload = async () => {
    if (items.length === 0 || downloading) return
    const slides = buildPublicPresentationSlides(proposta)
    if (slides.length === 0) {
      toast.error('Esta proposta não tem plano para exportar.')
      return
    }
    slidesRef.current = slides
    setDownloading(true)
    const toastId = toast.loading('Gerando apresentação em PDF...')
    const restore = () => setCaptureIndex(null)
    try {
      await downloadStudioPresentationPdf(empresaNome?.trim() || 'Cliente', {
        slideCount: slides.length,
        showSlide: async (index) => {
          setCaptureIndex(index)
          await waitForSlidePreview(index)
        },
        restore,
      })
      toast.success('Apresentação em PDF baixada!', { id: toastId })
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Não foi possível baixar o PDF'), { id: toastId })
    } finally {
      restore()
      setDownloading(false)
    }
  }

  const captureSlide = captureIndex !== null ? slidesRef.current[captureIndex] : null

  return (
    <aside className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden lg:sticky lg:top-8 shadow-sm">
      {captureSlide && captureIndex !== null ? (
        <PublicProposalPdfSlide slide={captureSlide} index={captureIndex} />
      ) : null}
      <div className="px-5 py-4 border-b border-[var(--border)] bg-[var(--bg-dark)]">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-[var(--brand-primary)]" />
          <h2 className="text-sm font-bold text-[var(--text-primary)]">Resumo do investimento</h2>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="space-y-2">
          {plans.map((plan) => (
            <div
              key={plan.item.id}
              className="flex items-start justify-between gap-3 text-xs border-b border-[var(--border)] pb-2 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="font-medium text-[var(--text-primary)] truncate">{plan.title}</p>
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{plan.tipoLabel}</p>
              </div>
              <p className="font-semibold text-[var(--text-primary)] shrink-0 tabular-nums">
                {formatCurrency(publicMonthlyAmount(plan.item))}/mês
              </p>
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-2 border-t border-[var(--border)] text-xs">
          {proposta.desconto > 0 && (
            <div className="flex justify-between text-red-400">
              <span className="inline-flex items-center gap-1">
                <Tag size={11} />
                Desconto ({proposta.desconto}%)
              </span>
              <span className="tabular-nums">-{formatCurrency(descontoValor)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-3 border-t border-[var(--border)]">
            <span className="text-sm font-bold text-[var(--text-primary)]">Valor mensal</span>
            <span className="text-xl font-bold text-[var(--brand-primary)] tabular-nums">
              {formatCurrency(valorMensal)}/mês
            </span>
          </div>
        </div>

        <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
          Valores válidos por {proposta.validade} dias a partir da data de emissão. Impostos inclusos
          conforme proposta comercial.
        </p>

        {onContratar && (
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            disabled={!canContratar}
            onClick={onContratar}
          >
            Aceitar Proposta
          </Button>
        )}

        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="w-full"
          icon={<Download size={14} />}
          loading={downloading}
          disabled={items.length === 0}
          onClick={() => void handleDownload()}
        >
          {downloading ? 'Gerando PDF...' : 'Baixar proposta (PDF)'}
        </Button>

        {proposta.observacoes && (
          <div className="rounded-xl bg-[var(--bg-dark)] border border-[var(--border)] p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
              Observações
            </p>
            <p className="text-[11px] text-[var(--text-primary)] whitespace-pre-line leading-relaxed">
              {proposta.observacoes}
            </p>
          </div>
        )}
      </div>
    </aside>
  )
}
